import { motion } from 'framer-motion';

// Floating geometric shapes for added depth
export default function FloatingShapes() {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {/* Large gradient orb - top left */}
      <motion.div
        className="absolute -top-40 -left-40 w-96 h-96 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)',
        }}
        animate={{
          x: [0, 30, 0],
          y: [0, 20, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Medium orb - top right */}
      <motion.div
        className="absolute -top-20 right-1/4 w-72 h-72 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.06) 0%, transparent 70%)',
        }}
        animate={{
          x: [0, -40, 0],
          y: [0, 30, 0],
          scale: [1, 1.15, 1],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Floating hexagon 1 */}
      <motion.div
        className="absolute top-1/4 left-[10%] w-20 h-20 opacity-[0.03]"
        animate={{
          y: [0, -30, 0],
          rotate: [0, 180, 360],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <svg viewBox="0 0 100 100" className="w-full h-full fill-blue-400">
          <polygon points="50,3 94,25 94,75 50,97 6,75 6,25" />
        </svg>
      </motion.div>

      {/* Floating hexagon 2 */}
      <motion.div
        className="absolute top-1/3 right-[15%] w-16 h-16 opacity-[0.04]"
        animate={{
          y: [0, 40, 0],
          rotate: [0, -180, -360],
          scale: [1, 0.9, 1],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <svg viewBox="0 0 100 100" className="w-full h-full fill-cyan-400">
          <polygon points="50,3 94,25 94,75 50,97 6,75 6,25" />
        </svg>
      </motion.div>

      {/* Floating square - rotated */}
      <motion.div
        className="absolute top-1/2 left-[20%] w-12 h-12 border border-blue-500/10 rounded-sm"
        animate={{
          y: [0, -50, 0],
          rotate: [45, 135, 45],
          opacity: [0.1, 0.2, 0.1],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Floating triangle */}
      <motion.div
        className="absolute bottom-1/3 right-[25%] w-14 h-14 opacity-[0.05]"
        animate={{
          y: [0, 30, 0],
          rotate: [0, 120, 0],
          scale: [1, 1.3, 1],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <svg viewBox="0 0 100 100" className="w-full h-full fill-purple-400">
          <polygon points="50,10 90,90 10,90" />
        </svg>
      </motion.div>

      {/* Large bottom orb */}
      <motion.div
        className="absolute -bottom-40 left-1/3 w-[500px] h-[500px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.05) 0%, transparent 70%)',
        }}
        animate={{
          x: [0, 50, 0],
          y: [0, -30, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Floating ring 1 */}
      <motion.div
        className="absolute top-[60%] left-[5%] w-24 h-24 border border-blue-500/5 rounded-full"
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.1, 0.05, 0.1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Floating ring 2 */}
      <motion.div
        className="absolute top-[20%] right-[8%] w-32 h-32 border border-purple-500/5 rounded-full"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.05, 0.1, 0.05],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2,
        }}
      />

      {/* Small floating dots */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-blue-400/20 rounded-full"
          style={{
            left: `${15 + i * 15}%`,
            top: `${20 + (i % 3) * 25}%`,
          }}
          animate={{
            y: [0, -20 - i * 5, 0],
            opacity: [0.2, 0.5, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 5 + i * 2,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.5,
          }}
        />
      ))}

      {/* Gradient lines - data flow effect */}
      <motion.div
        className="absolute top-0 left-1/4 w-px h-full"
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, rgba(59, 130, 246, 0.1) 30%, rgba(59, 130, 246, 0.1) 70%, transparent 100%)',
        }}
        animate={{
          opacity: [0, 0.5, 0],
          y: ['-100%', '100%'],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'linear',
        }}
      />

      <motion.div
        className="absolute top-0 right-1/3 w-px h-full"
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, rgba(139, 92, 246, 0.08) 40%, rgba(139, 92, 246, 0.08) 60%, transparent 100%)',
        }}
        animate={{
          opacity: [0, 0.4, 0],
          y: ['-100%', '100%'],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'linear',
          delay: 3,
        }}
      />
    </div>
  );
}
